import { useEffect, useMemo, useState } from 'react';
import { NotificationsProvider } from '../../notifications/Notifications';
import { ElementSettingsModal } from './ElementSettingsModal';
import { Template } from './Template';
import { createRenderRuntime, reportElementClasses } from './elements/registry';
import type { PageSettings } from './pageSettings';
import type { ReportStructureController } from './reportStructureController';
import { useProfile } from '../../../profile';
import type {
    ElementDefinition,
    ElementSettingsEditRequest,
    RenderContext,
    RenderRuntimeGraphSettings,
    RenderRuntimeDataServices,
} from './elements/types';

interface OpenSettingsState {
    request: ElementSettingsEditRequest;
    definition: ElementDefinition;
}

interface TemplateEditorProps {
    structure: ElementDefinition[];
    context: RenderContext;
    structureController: ReportStructureController;
    pageSettings: PageSettings;
    graphSettings?: RenderRuntimeGraphSettings;
    runtimeDataServices: RenderRuntimeDataServices;
}

export function TemplateEditor({
    structure,
    context,
    structureController,
    pageSettings,
    graphSettings,
    runtimeDataServices,
}: TemplateEditorProps): JSX.Element {
    const profile = useProfile();
    const [openSettings, setOpenSettings] = useState<OpenSettingsState | null>(null);
    const runtime = useMemo(
        () =>
            createRenderRuntime(
                reportElementClasses,
                true,
                pageSettings,
                graphSettings,
                runtimeDataServices,
                'editor',
                profile,
                structureController
            ),
        [pageSettings, graphSettings, runtimeDataServices, profile, structureController]
    );

    useEffect(() => {
        const controller = runtime.editor.structureController;

        function handleSettingsEditRequest(request: ElementSettingsEditRequest): void {
            const definition = controller.getElementAtPath(request.location.elementPath);
            setOpenSettings({
                request,
                definition,
            });
        }

        controller.setRequestSettingsEditHandler(handleSettingsEditRequest);

        return () => {
            controller.setRequestSettingsEditHandler(null);
        };
    }, [runtime]);

    function closeSettings(): void {
        setOpenSettings(null);
    }

    function saveSettings(nextSettings: Record<string, unknown>): void {
        if (!openSettings) {
            return;
        }

        runtime.editor.structureController.replaceSettingsAtPath(
            openSettings.request.location.elementPath,
            nextSettings
        );
        setOpenSettings(null);
    }

    return (
        <>
            <div className="bg-gray-100 py-6">
                <div className="flex w-full flex-col items-center gap-6">
                    <Template structure={structure} context={context} runtime={runtime} />
                </div>
            </div>
            {openSettings ? (
                <NotificationsProvider registerGlobalRef={false}>
                    <ElementSettingsModal
                        definition={openSettings.definition}
                        contextKind={openSettings.request.location.contextKind}
                        context={openSettings.request.context}
                        runtimeDataServices={runtimeDataServices}
                        onClose={closeSettings}
                        onSave={saveSettings}
                    />
                </NotificationsProvider>
            ) : null}
        </>
    );
}
